/**
 * Platform redirect utilities for smart deep linking
 * Handles mobile app deep links with web fallback
 */

export type SocialPlatform = 
  | 'instagram' 
  | 'tiktok' 
  | 'linkedin' 
  | 'twitter' 
  | 'facebook' 
  | 'youtube' 
  | 'telegram' 
  | 'quora' 
  | 'medium' 
  | 'linktree'
  | 'substack';

/**
 * Detect if user is on a mobile device
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Check for mobile patterns
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const isMobile = mobileRegex.test(userAgent.toLowerCase());
  
  // Additional check for touch support
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return isMobile || (hasTouch && window.innerWidth < 768);
}

/**
 * Get deep link URL for mobile apps
 */
export function getPlatformDeepLink(
  platform: SocialPlatform, 
  handle?: string
): string | null {
  if (!handle) return null;
  
  const cleanHandle = handle.replace(/^@/, '');
  
  const deepLinks: Record<SocialPlatform, string | null> = {
    instagram: `instagram://user?username=${cleanHandle}`,
    tiktok: `tiktok://user?username=${cleanHandle}`,
    linkedin: `linkedin://in/${cleanHandle}`, // LinkedIn deep links require profile ID, fallback to general
    twitter: `twitter://user?screen_name=${cleanHandle}`,
    facebook: `fb://`, // Facebook deep links require profile ID, fallback to general
    youtube: `vnd.youtube://user/${cleanHandle}`,
    telegram: `tg://resolve?domain=${cleanHandle}`,
    quora: null, // No reliable deep link
    medium: null, // No reliable deep link
    linktree: null, // No deep link
    substack: null, // No deep link
  };
  
  return deepLinks[platform];
}

/**
 * Extract handle from profile URL
 */
export function extractHandleFromProfileUrl(url: string): string | undefined {
  if (!url) return undefined;
  
  try {
    // Remove protocol and www
    const cleanUrl = url.replace(/^https?:\/\/(www\.)?/, '');
    
    // Extract handle after domain
    const parts = cleanUrl.split('/');
    if (parts.length > 1) {
      // Get the part after domain, remove @ if present
      return parts[1].replace(/^@/, '');
    }
  } catch (error) {
    console.error('Error extracting handle:', error);
  }
  
  return undefined;
}

/**
 * Get web URL for platform
 */
export function getPlatformWebUrl(
  platform: SocialPlatform,
  profileUrl?: string,
  handle?: string
): string {
  
  // Otherwise construct from handle
  const cleanHandle = handle?.replace(/^@/, '') || '';
  
  const baseUrls: Record<SocialPlatform, string> = {
    instagram: `https://www.instagram.com/accounts/edit/`,
    tiktok: `https://www.tiktok.com/profile`,
    linkedin: `https://www.linkedin.com/in/edit/`,
    twitter: `https://x.com/settings/profile`,
    facebook: `https://www.facebook.com/profile.php?fb_profile_edit_entry_point={"click_point":"edit_profile_button","feature":"profile_header"}&sk=about`,
    youtube: `https://studio.youtube.com/channel/CHANNEL_ID/editing/profile`,
    telegram: `https://t.me/${cleanHandle}`,
    quora: `https://quora.com/profile/${cleanHandle}`,
    medium: `https://medium.com/@${cleanHandle}`,
    linktree: `https://linktr.ee/${cleanHandle}`,
    substack: `https://${cleanHandle}.substack.com/`,
  };
  
  return baseUrls[platform];
}

/**
 * Smart platform URL opener with deep link fallback
 * Tries mobile app first, falls back to web
 */
export async function openPlatformUrl(
  platform: SocialPlatform,
  profileUrl?: string,
  handle?: string
): Promise<void> {
  const isMobile = isMobileDevice();
  const extractedHandle = handle || extractHandleFromProfileUrl(profileUrl || '');
  const webUrl = getPlatformWebUrl(platform, profileUrl, extractedHandle);
  
  // On mobile, try deep link first
  if (isMobile && extractedHandle) {
    const deepLink = getPlatformDeepLink(platform, extractedHandle);
    
    if (deepLink) {
      try {
        // Try to open deep link
        window.location.href = deepLink;
        
        // Fallback to web URL after short delay if app not installed
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Check if page is still visible (app didn't open)
        if (!document.hidden) {
          window.open(webUrl, '_blank', 'noopener,noreferrer');
        }
        
        return;
      } catch (error) {
        console.warn('Deep link failed, falling back to web:', error);
      }
    }
  }
  
  // Default: open web URL
  window.open(webUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Get platform edit/profile page URL
 * Returns the URL where users can edit their profile
 */
export function getPlatformEditUrl(platform: SocialPlatform): string {
  const editUrls: Record<SocialPlatform, string> = {
    instagram: 'https://www.instagram.com/accounts/edit/',
    tiktok: 'https://www.tiktok.com/setting',
    linkedin: 'https://www.linkedin.com/in/edit/',
    twitter: 'https://twitter.com/settings/profile',
    facebook: 'https://www.facebook.com/profile.php',
    youtube: 'https://studio.youtube.com/channel/UC/editing/details',
    telegram: 'https://telegram.org/',
    quora: 'https://www.quora.com/profile/edit',
    medium: 'https://medium.com/me/settings',
    linktree: 'https://linktr.ee/admin/settings',
    substack: 'https://substack.com/settings',
  };
  
  return editUrls[platform];
}
